import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import buildingIcon from '@assets/images/calendar-building-icon.svg'
import floorIcon from '@assets/images/calendar-floor-icon.svg'
import zoneIcon from '@assets/images/calendar-zone-icon.svg'
import CheckIcon from '@assets/images/check-icon.svg?react'

import { Zone, ZoneType } from '@types'

import { Option, Select } from '@components/Select'

import { useResolveZone } from '@hooks/zone/resolve-zone.hook'
import { useZoning } from '@hooks/zone/zoning.hook'

import {
  CheckedCircle,
  IconContainer,
  Img,
  ItemContainer,
  ItemLabel,
  SelectedItemLabel,
  WrappedComponentContainer,
} from './SelectZone.styled'

interface Props {
  required?: boolean
  projectId: string
  zone?: Zone
  onSelectZone: (zone?: Zone) => void
  WrappedComponent?: () => JSX.Element
  disabledSearch?: boolean
  disableLinkedZone?: boolean
}

const zoneIcons: Record<ZoneType, string> = {
  BUILDING: buildingIcon,
  FLOOR: floorIcon,
  ZONE: zoneIcon,
}

export const SelectZone = (props: Props): JSX.Element => {
  const {
    required,
    projectId,
    zone,
    disabledSearch = false,
    onSelectZone,
    WrappedComponent,
    disableLinkedZone = false,
  } = props

  const { t } = useTranslation()

  const [search, setSearch] = useState<string>(zone?.name || '')
  const { getSortedZoning } = useResolveZone()

  useEffect(() => {
    setSearch(zone?.name || '')
  }, [zone, WrappedComponent])

  const { data } = useZoning(projectId)

  const selectedOption: Option | undefined = zone && {
    id: zone.id,
    label: zone.name,
    data: zone,
    ...(disabledSearch
      ? {
          Component: () => (
            <ItemContainer>
              <SelectedItemLabel>{zone.name}</SelectedItemLabel>
            </ItemContainer>
          ),
        }
      : {}),
  }

  const options: Option[] = useMemo(() => {
    const allZones = getSortedZoning(data?.zoning || [])

    return allZones.map((_zone) => ({
      id: _zone.id,
      label: _zone.name,
      data: _zone,
      disabled: disableLinkedZone && _zone.plans.length > 0,
      Component: () => (
        <ItemContainer disabled={disableLinkedZone && _zone.plans.length > 0}>
          <Img src={zoneIcons[_zone.type]} type={_zone.type} />

          <ItemLabel selected={_zone.id == zone?.id}>{_zone.name}</ItemLabel>

          {_zone.id == zone?.id && (
            <IconContainer>
              <CheckIcon />
            </IconContainer>
          )}
        </ItemContainer>
      ),
    })) as Option[]
  }, [data, zone, getSortedZoning, disableLinkedZone])

  const filteredOptions = WrappedComponent
    ? options
    : options.filter((option) => option.label.toLowerCase().indexOf((search || '').toLowerCase()) !== -1)

  return (
    <Select
      disabledVerticalCorrection
      WrappedComponent={
        WrappedComponent
          ? ({ show }) => (
              <WrappedComponentContainer onClick={show}>
                <WrappedComponent />
                {selectedOption && <CheckedCircle />}
              </WrappedComponentContainer>
            )
          : undefined
      }
      required={required}
      label={t('modules_shared_components_SelectZone_label')}
      emptyLabel={t('modules_shared_components_SelectZone_empty_label')}
      placeholder={t('modules_shared_components_SelectZone_placeholder')}
      search={disabledSearch ? undefined : search}
      onSearch={disabledSearch ? undefined : setSearch}
      selected={selectedOption}
      options={disabledSearch ? options : filteredOptions}
      onClick={(option) => {
        if (option) {
          setSearch(option.label)
          onSelectZone(option.data as Zone)
        } else {
          setSearch('')
          onSelectZone(undefined)
        }
      }}
    />
  )
}
